import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import FocusTrap from 'focus-trap-react';
import { Portal } from 'react-portal';
import { usePathname } from 'next/navigation';
import { Search as SearchIcon, X } from 'lucide-react';
import { fuseSearch } from '@/api/searchIndex';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ICategoryNews,
  ISearchNewsResult,
  ISearchResult,
  ISearchResultItem,
  ISearchResultResort,
  SearchCategory,
} from '@/api/home/types';
import { I18nKey } from '@/i18n/types';
import SearchLink from '@/components/home/SearchPopup/SearchLink';
import { fetchNewsSearch } from '@/api/home/static';
import { sortByKeyExisting } from '@/util/sortByKeyExisting';
import { useShouldAdjustForSummer } from '@/util/useShouldAdjustForSummer';
import { Routes } from '@/routes';
import { htmlDecode } from '@/util/htmlDecode';
import { containerClass, Container } from '@/components/ui/container';
import { focusWithSoftKeyboard } from '@/util/softKeyboard';
import { cn } from '@/util/cn';

// Full-screen search overlay. Drives the mobile search UX (winter +
// summer header burger / search icon) and the legacy fallback for
// the home / 404 hero search bar. Desktop now uses the inline
// HeaderSearch dropdown instead — this popup only opens on mobile or
// from the standalone <Search /> component.
//
// v3 rewrite: drops the 294-line SCSS module and the 5 per-section
// component files (SearchBar, SearchResultsColumn, SearchNewsColumn,
// NewsCategories, NoResults). Their JSX lives inline below as small
// render helpers. SearchLink is the only sibling file kept — it
// holds the analytics-wrapped Link that result rows share.
//
// labelMap is re-exported for RateResortSearch's SearchResultsPopUp
// (which uses the same i18n keys for its column headers).

export const labelMap: Record<SearchCategory, I18nKey['common']> = {
  [SearchCategory.resorts]: 'searchResults.resorts',
  [SearchCategory.regions]: 'searchResults.regions',
  [SearchCategory.news]: 'searchResults.news',
  [SearchCategory.other]: 'common.dash',
};

const formatTitle = (item: ISearchResultItem): string => {
  if (item.categoryUuid !== SearchCategory.resorts) {
    return item.title;
  }
  const resortItem = item as ISearchResultResort;
  const parts = [item.title];
  if (resortItem.regionTitle) parts.push(resortItem.regionTitle);
  if (resortItem.resort.country_code) {
    parts.push(resortItem.resort.country_code.split('-')[0]);
  }
  return parts.join(', ');
};

const resolveHref = (item: ISearchResultItem, isSummer: boolean): string => {
  if (item.categoryUuid === SearchCategory.resorts) {
    const r = item as ISearchResultResort;
    return isSummer
      ? Routes.resortTripPlanningOverview(r.resort)
      : Routes.resortSnowReport(r.resort);
  }
  if (item.categoryUuid === SearchCategory.regions) {
    return Routes.regionSnowReport(item.region);
  }
  if (item.categoryUuid === SearchCategory.other) {
    return item.href;
  }
  return '';
};

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <h2 className="mb-3 text-[0.7rem] font-semibold uppercase tracking-wider text-muted-foreground">
    {children}
  </h2>
);

const NoResults = () => {
  const { t } = useOTSTranslation('common');
  return (
    <div className="px-3 py-3 text-sm text-muted-foreground/70">
      {t('searchResults.noResultsFound')}
    </div>
  );
};

// Placeholder rows that mirror the result-row geometry (left-aligned
// label, right-aligned tag for the resort/region column, no tag for
// the news column).
const ResultsColumnSkeleton = ({ newsShape = false }: { newsShape?: boolean }) => (
  <div>
    <Skeleton className="mb-3 h-3 w-24" />
    <ul className="flex flex-col">
      {[0, 1, 2, 3, 4].map((i) => (
        <li key={i} className="flex items-center justify-between gap-3 px-3 py-2.5">
          <Skeleton className={`h-4 ${i % 2 === 0 ? 'w-3/5' : 'w-2/3'}`} />
          {!newsShape && <Skeleton className="h-4 w-12 rounded-md" />}
        </li>
      ))}
    </ul>
  </div>
);

const Tag = ({ children }: { children: React.ReactNode }) => (
  <span
    className={cn(
      'shrink-0 rounded-md border border-transparent bg-[#8090a0] px-2 py-0.5',
      'text-[0.7rem] font-medium uppercase tracking-wide text-white',
    )}
  >
    {children}
  </span>
);

type ResultsColumnProps = {
  label: string;
  items: Array<ISearchResultItem>;
  query: string;
  focusedIndex: number;
  shouldFollowFocusedLink: boolean;
  resolveHref: (item: ISearchResultItem) => string;
};

const ResultsColumn = ({
  label, items, query, focusedIndex, shouldFollowFocusedLink, resolveHref: resolve,
}: ResultsColumnProps) => {
  const { t } = useOTSTranslation('common');
  return (
    <div>
      <SectionHeading>{label}</SectionHeading>
      {items.length === 0 ? <NoResults /> : (
        <ul className="flex flex-col">
          {items.map((item, index) => {
            const isOther = item.categoryUuid === SearchCategory.other;
            const isFocused = index === focusedIndex;
            const href = resolve(item);
            return (
              <li key={item.uuid}>
                <SearchLink
                  query={query}
                  href={href}
                  shouldFollowFocusedLink={isFocused && shouldFollowFocusedLink}
                  className={cn(
                    'flex items-center justify-between gap-3 rounded-md px-3 py-2.5',
                    'text-sm text-foreground no-underline transition-colors',
                    'hover:bg-primary hover:text-white',
                    isFocused && 'bg-muted',
                  )}
                >
                  <span className="break-words">{formatTitle(item)}</span>
                  {!isOther && (
                    <Tag>{t(`searchResults.${item.categoryUuid.slice(0, -1)}` as I18nKey['common'])}</Tag>
                  )}
                </SearchLink>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

type NewsColumnProps = {
  label: string;
  list: Array<{ uuid: string; news: { title: string; link?: string; categories?: ICategoryNews[] | ICategoryNews | null } }> | undefined;
  loading: boolean;
  query: string;
};

const NewsCategories = ({
  categories, searchResultFor,
}: {
  categories: Array<ICategoryNews> | ICategoryNews | undefined | null;
  searchResultFor: string;
}) => {
  if (!categories) return null;
  const tags: Array<ICategoryNews> = Array.isArray(categories) ? categories : [categories];
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {tags.map((tag) => (
        <SearchLink
          key={tag.uuid}
          query={searchResultFor}
          href={tag.link ?? ''}
          className="no-underline"
        >
          <Tag>{tag.title}</Tag>
        </SearchLink>
      ))}
    </div>
  );
};

const NewsColumn = ({
  label, list, loading, query,
}: NewsColumnProps) => (
  <div>
    <SectionHeading>{label}</SectionHeading>
    {loading && (
      <ul className="flex flex-col">
        {[0, 1, 2, 3].map((i) => (
          <li key={i} className="px-3 py-2.5">
            <Skeleton className="h-4 w-3/4" />
          </li>
        ))}
      </ul>
    )}
    {!loading && (!list || list.length === 0) && <NoResults />}
    {!loading && list && list.length > 0 && (
      <ul className="flex flex-col">
        {list.map((item) => (
          <li key={item.uuid} className="rounded-md px-3 py-2.5 text-sm hover:bg-muted">
            <SearchLink
              href={item.news.link ?? ''}
              query={query}
              className="block text-foreground no-underline hover:text-primary hover:underline"
            >
              {htmlDecode(item.news.title)}
            </SearchLink>
            {item.news.categories && (
              <NewsCategories categories={item.news.categories} searchResultFor={query} />
            )}
          </li>
        ))}
      </ul>
    )}
  </div>
);

export const SearchPopup = ({
  closePopup,
  query,
}: {
  closePopup: () => void,
  query?: string | string[] | undefined,
}) => {
  const { t } = useOTSTranslation('common');
  const pathname = usePathname();
  const hasNavigated = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isSummer = useShouldAdjustForSummer();
  const [isLoading, toggleLoading] = useState<boolean>(false);
  const [newsLoading, setNewsLoading] = useState<boolean>(false);
  const [searchResult, setSearchResult] = useState<ISearchResult>();
  const [newsSearchResult, setNewsSearchResult] = useState<ISearchNewsResult>();
  const [searchResultFor, setSearchResultFor] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const [shouldFollowFocusedLink, setShouldFollowFocusedLink] = useState(false);

  const getSearchResult = useCallback(async (e?:FormEvent<HTMLFormElement> | undefined, forceQuery?: string) => {
    if (e) e.preventDefault();
    toggleLoading(true);
    if (forceQuery) setSearchValue(forceQuery);
    const searchResponse = await fuseSearch(forceQuery || searchValue, undefined, t);
    setSearchResult(searchResponse);
    setSearchResultFor(forceQuery || searchValue);
    toggleLoading(false);
  }, [searchResult, searchValue]);

  const getNewsResult = useCallback(async (forceQuery?: string) => {
    setNewsLoading(true);
    const news = await fetchNewsSearch(forceQuery || searchValue);
    setNewsSearchResult(news);
    setNewsLoading(false);
  }, [newsSearchResult, searchValue]);

  const handleInputChange = useCallback((e: React.SyntheticEvent) => {
    if (debounce.current) clearTimeout(debounce.current);
    const target = e.target as HTMLInputElement;
    const val = target.value;
    setSearchValue(val);
    debounce.current = setTimeout(() => {
      getSearchResult(undefined, val).catch(() => {});
      getNewsResult(val).catch(() => {});
    }, 200);
  }, [searchValue, getSearchResult]);

  useEffect(() => {
    if (query) {
      const newQuery = Array.isArray(query) ? query[0] : query;
      setSearchValue(newQuery);
      getSearchResult(undefined, newQuery).catch(() => {});
      getNewsResult(newQuery).catch(() => {});
    }
  }, [query]);

  useEffect(() => {
    document.body.classList.add('search-overlay');
    return () => {
      document.body.classList.remove('search-overlay');
    };
  }, []);

  // Put the caret in the search field the moment the overlay opens, and
  // take the on-screen keyboard over from the primer the opener raised
  // (see util/softKeyboard) so mobile users can type straight away
  // instead of tapping the field a second time.
  useEffect(() => {
    focusWithSoftKeyboard(inputRef.current);
  }, []);

  // App Router has no router.events; close the popup once the route changes
  // (the first run is the initial mount, which must not close it).
  useEffect(() => {
    if (hasNavigated.current) {
      closePopup();
    } else {
      hasNavigated.current = true;
    }
  }, [pathname, closePopup]);

  // Merge resort + region + other results into a single flat list
  // sorted by domain priority (matching the legacy ordering).
  const filteredSearchResults = useMemo(() => {
    const others = searchResult?.results.other || [];
    const resorts = searchResult?.results.resorts || [];
    const regions = searchResult?.results.regions || [];
    const [showFirstResorts, showLastResorts] = sortByKeyExisting(resorts, 'resort.domain');
    const [showFirstRegions, showLastRegions] = sortByKeyExisting(regions, 'region.domain');
    return showFirstRegions.concat(others, showFirstResorts, showLastRegions, showLastResorts);
  }, [searchResult, searchResultFor]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement> | undefined) => {
    if (!e) return;
    if (e.code === 'Escape') {
      closePopup();
      return;
    }
    if (e.code === 'Enter') {
      setShouldFollowFocusedLink(true);
      return;
    }
    const prevFocusedIndex = focusedIndex;
    let newFocusedIndex = focusedIndex;
    if (e.code === 'ArrowUp') newFocusedIndex -= 1;
    if (e.code === 'ArrowDown') newFocusedIndex += 1;
    const isInBounds = newFocusedIndex >= 0 && newFocusedIndex < filteredSearchResults.length;
    if (!isInBounds) {
      const prevInBounds = prevFocusedIndex >= 0 && prevFocusedIndex < filteredSearchResults.length;
      newFocusedIndex = prevInBounds ? prevFocusedIndex : 0;
    }
    setFocusedIndex(newFocusedIndex);
  }, [focusedIndex, filteredSearchResults.length, setFocusedIndex]);

  const helpText = !searchResult && !isLoading && !newsSearchResult;
  const hasAnyResults = filteredSearchResults.length > 0;

  return (
    <Portal>
      {/* The trap's own initial focus has to land on the search field
          without a timer: focus-trap defaults to deferring it, and a
          deferred focus is exactly what stops iOS from raising the
          keyboard. `preventScroll` keeps the body from jumping under
          the fixed overlay. */}
      <FocusTrap
        focusTrapOptions={{
          initialFocus: () => inputRef.current ?? false,
          delayInitialFocus: false,
          preventScroll: true,
        }}
      >
        <div
          role="dialog"
          aria-hidden="true"
          tabIndex={-1}
          className="fixed inset-0 z-[13] flex flex-col bg-white"
          onKeyDown={handleKeyDown}
        >
          {/* ─── Top bar ──────────────────────────────────────────── */}
          <header className="flex h-[64px] items-center bg-primary">
            <Container width="xl-only" className="flex items-center gap-3 !px-4">
              <SearchIcon size={20} className="shrink-0 fill-none text-white" aria-hidden />
              <form className="flex-1" onSubmit={(e) => e.preventDefault()}>
                <input
                  ref={inputRef}
                  type="text"
                  placeholder={t('searchResults.helptext')}
                  value={searchValue}
                  onChange={handleInputChange}
                  // Mobile keyboard hints: a search-shaped return key,
                  // and none of the autocorrect/autocapitalise fixups
                  // that mangle resort names as you type them.
                  enterKeyHint="search"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  className={cn(
                    'block w-full appearance-none border-0 border-b border-white/40 bg-transparent',
                    'pb-1 pt-0 text-base font-semibold leading-tight text-white outline-none',
                    'placeholder:font-normal placeholder:text-white/50',
                    'focus:border-white',
                  )}
                />
              </form>
              <button
                type="button"
                aria-label={t('home.searchPopup.close')}
                onClick={closePopup}
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded',
                  'text-white transition-colors hover:bg-white/10',
                )}
              >
                <X size={22} strokeWidth={2.25} />
              </button>
            </Container>
          </header>

          {/* ─── Body ─────────────────────────────────────────────── */}
          <section className="flex-1 overflow-auto pb-[env(safe-area-inset-bottom)]">
            <div className={cn(containerClass, 'px-4 md:px-0')}>
              {helpText && (
                <h2 className="mt-12 text-center text-base font-medium text-foreground md:mt-20">
                  {t('searchResults.helptext')}
                </h2>
              )}

              {isLoading && (
                <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-2">
                  <ResultsColumnSkeleton />
                  <ResultsColumnSkeleton newsShape />
                </div>
              )}

              {!isLoading && (
                <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-2">
                  {hasAnyResults && (
                    <ResultsColumn
                      label={`${t(labelMap[SearchCategory.regions])} & ${t(labelMap[SearchCategory.resorts])}`}
                      items={filteredSearchResults}
                      query={searchResultFor}
                      focusedIndex={focusedIndex}
                      shouldFollowFocusedLink={shouldFollowFocusedLink}
                      resolveHref={(it) => resolveHref(it, isSummer)}
                    />
                  )}
                  {searchResult && (
                    <NewsColumn
                      label={t(labelMap[SearchCategory.news])}
                      list={newsSearchResult?.results}
                      loading={newsLoading}
                      query={searchResultFor}
                    />
                  )}
                </div>
              )}
            </div>
          </section>
        </div>
      </FocusTrap>
    </Portal>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────
